import { UmsMember, UmsMemberMail } from 'types/ums';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getUmsMemberMailList } from 'apis/ums';
import usePagination from 'commons/hooks/usePagination';
import { PageQuery } from 'types/base';
import Table from 'antd/es/table';
import formatTime from 'utils/formatTime';
import showTotal from 'components/show-total';
import { Button, Card, Modal } from 'antd';
import i18n from '../../../i18n';
import LOCALS from '../../../commons/locals';
import { Mail } from 'lucide-react';
import DOMPurify from 'dompurify';

const MemberMailHistory = ({ memberId }: { memberId: UmsMember['id'] }) => {
  const [content, setContent] = useState('');
  const {
    loading,
    setLoading,
    pageNum,
    setPageNum,
    pageSize,
    setPageSize,
    total,
    setTotal,
    dataSource,
    setDataSource,
  } = usePagination<UmsMemberMail>();

  const getDataSource = useCallback(
    async ({ pageNum, pageSize }: PageQuery) => {
      try {
        setLoading(true);
        const { list, total } = await getUmsMemberMailList({
          memberId,
          pageNum,
          pageSize,
        });

        setDataSource(list);
        setTotal(total);
      } catch (err) {
      } finally {
        setLoading(false);
      }
    },
    [memberId, setDataSource, setLoading, setTotal]
  );

  useEffect(() => {
    getDataSource({ pageNum: 1, pageSize: 10 });
  }, [getDataSource]);

  const columns = useMemo(() => {
    return [
      {
        title: i18n.t(LOCALS.subject),
        key: 'subject',
        dataIndex: 'subject',
      },
      {
        title: i18n.t(LOCALS.content),
        key: 'content',
        dataIndex: 'content',
        render: (content: UmsMemberMail['content']) => {
          return (
            <Button
              type="link"
              onClick={() => {
                setContent(content);
              }}
            >
              {i18n.t(LOCALS.view)}
            </Button>
          );
        },
      },
      {
        title: i18n.t(LOCALS.created_time),
        dataIndex: 'createTime',
        key: 'createTime',
        render: (createTime: UmsMemberMail['createTime']) => {
          return formatTime(createTime);
        },
      },
      {
        title: i18n.t(LOCALS.send_time),
        dataIndex: 'sendTime',
        key: 'sendTime',
        render: (sendTime: UmsMemberMail['sendTime']) => {
          return formatTime(sendTime);
        },
      },
    ];
  }, []);

  return (
    <Card bodyStyle={{ padding: '16px' }}>
      <div className="flex items-center gap-2 mb-4 text-base font-medium">
        <Mail className="w-5 h-5" />
        {i18n.t(LOCALS.email_history)}
      </div>

      <Modal
        open={!!content}
        onCancel={() => {
          setContent('');
        }}
      >
        <p
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
        ></p>
      </Modal>

      <Table
        bordered
        tableLayout="auto"
        scroll={{ x: 'max-content' }}
        pagination={{
          showTotal,
          total,
          pageSize,
          current: pageNum,
          onChange: (page, pageSize) => {
            setPageNum(page);
            setPageSize(pageSize);
            getDataSource({ pageNum: page, pageSize });
          },
        }}
        loading={loading}
        rowKey={'id'}
        dataSource={dataSource}
        columns={columns}
      />
    </Card>
  );
};

export default MemberMailHistory;
